import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { BRAND_KEYS, DEFAULT_BRAND_KEY } from '../../config/brands';
import { BRAND_PROFILES } from '../../config/brandProfiles';
import { brandKeyOf } from '../utils/brandFilter';

interface ParentRow {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  active?: boolean | null;
}

interface StudentRow {
  id: string;
  first_name: string;
  last_name: string;
  active?: boolean | null;
  metadata?: {
    labels?: string[];
  } | null;
  student_parents: Array<{
    parent: ParentRow | ParentRow[] | null;
  }>;
}

interface ProgramBrandType {
  type?: string | null;
  brand_key?: string | null;
}

/**
 * Built-in list keys for each non-default brand's current families. The enrichment and
 * after-school lists below are the default brand's; these are how another brand's families are
 * reached without writing to the default brand's.
 */
const brandListKey = (brand: string) => `__virtual_current_brand_${brand}`;
const OTHER_BRAND_LISTS = BRAND_KEYS.filter((key) => key !== DEFAULT_BRAND_KEY).map((key) => ({
  brand: key,
  label: `Current ${BRAND_PROFILES[key].shortName} Students`,
  labelKey: brandListKey(key),
}));

interface EnrollmentRow {
  student_id: string | null;
  is_active?: boolean | null;
  programs?: ProgramBrandType | ProgramBrandType[] | null;
}

interface LabelEmailEntry {
  label: string;
  labelKey: string;
  students: Array<{
    id: string;
    name: string;
    parentEmails: string[];
  }>;
  emails: string[];
}

// "Active Students" is every active child, whichever brand; the enrichment and after-school
// lists are the default brand's programs only. Before brands existed "enrichment" meant "not
// after-school", which would have put every Kaleido family on a BHCS enrichment mailing.
const DEFAULT_SHORT_NAME = BRAND_PROFILES[DEFAULT_BRAND_KEY].shortName;
const VIRTUAL_LABELS = [
  { label: 'Active Students (all brands)', labelKey: '__virtual_active_students' },
  { label: `Current ${DEFAULT_SHORT_NAME} Enrichment Students`, labelKey: '__virtual_current_enrichment' },
  { label: `Current ${DEFAULT_SHORT_NAME} After School Students`, labelKey: '__virtual_current_after_school' },
  ...OTHER_BRAND_LISTS.map(({ label, labelKey }) => ({ label, labelKey })),
];

export function AdminEmailLists() {
  const [entries, setEntries] = useState<LabelEmailEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedLabel, setCopiedLabel] = useState<string | null>(null);
  const [selectedLabelKeys, setSelectedLabelKeys] = useState<string[]>([]);


  useEffect(() => {
    const fetchLabelEmails = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('students')
          .select('id, first_name, last_name, active, metadata, student_parents(parent:parents(id, email, first_name, last_name, active))');

        if (fetchError) throw fetchError;

        const { data: enrollmentData, error: enrollmentError } = await supabase
          .from('enrollments')
          .select('student_id, is_active, programs(type, brand_key)');

        if (enrollmentError) throw enrollmentError;

        const rows = (data || []) as StudentRow[];
        const enrollments = (enrollmentData || []) as EnrollmentRow[];
        const enrichmentStudentIds = new Set<string>();
        const afterSchoolStudentIds = new Set<string>();
        const studentIdsByOtherBrand = new Map<string, Set<string>>(
          OTHER_BRAND_LISTS.map(({ brand }) => [brand, new Set<string>()]),
        );

        for (const enrollment of enrollments) {
          if (!enrollment.student_id) continue;
          if (!enrollment.is_active) continue;
          const programData = Array.isArray(enrollment.programs) ? enrollment.programs[0] : enrollment.programs;
          const brand = brandKeyOf(programData?.brand_key);
          if (brand !== DEFAULT_BRAND_KEY) {
            studentIdsByOtherBrand.get(brand)?.add(enrollment.student_id);
            continue;
          }
          if (programData?.type === 'after-school') {
            afterSchoolStudentIds.add(enrollment.student_id);
          } else {
            enrichmentStudentIds.add(enrollment.student_id);
          }
        }

        const labelMap = new Map<
          string,
          {
            label: string;
            labelKey: string;
            students: Map<string, { id: string; name: string; parentEmails: Set<string> }>;
            emails: Set<string>;
          }
        >();

        for (const virtualLabel of VIRTUAL_LABELS) {
          labelMap.set(virtualLabel.labelKey, {
            label: virtualLabel.label,
            labelKey: virtualLabel.labelKey,
            students: new Map<string, { id: string; name: string; parentEmails: Set<string> }>(),
            emails: new Set<string>(),
          });
        }

        for (const student of rows) {
          const rawLabels = student.metadata?.labels;
          const labels = Array.isArray(rawLabels) ? rawLabels : [];
          const emailsForStudent: string[] = [];
          for (const rel of student.student_parents || []) {
            const parents = Array.isArray(rel.parent)
              ? rel.parent
              : rel.parent
                ? [rel.parent]
                : [];
            for (const parent of parents) {
              if (!parent?.email) continue;
              if (parent.active === false) continue;
              emailsForStudent.push(parent.email.trim().toLowerCase());
            }
          }

          const studentName = `${student.first_name} ${student.last_name}`.trim();
          const addStudentToLabel = (label: string, labelKey: string) => {
            if (!labelMap.has(labelKey)) {
              labelMap.set(labelKey, {
                label,
                labelKey,
                students: new Map<string, { id: string; name: string; parentEmails: Set<string> }>(),
                emails: new Set<string>(),
              });
            }

            const entry = labelMap.get(labelKey)!;
            if (!entry.students.has(student.id)) {
              entry.students.set(student.id, {
                id: student.id,
                name: studentName,
                parentEmails: new Set<string>(),
              });
            }
            const studentEntry = entry.students.get(student.id)!;
            for (const email of emailsForStudent) {
              entry.emails.add(email);
              studentEntry.parentEmails.add(email);
            }
          };

          if (student.active) {
            addStudentToLabel(VIRTUAL_LABELS[0].label, '__virtual_active_students');
          }
          if (enrichmentStudentIds.has(student.id)) {
            addStudentToLabel(VIRTUAL_LABELS[1].label, '__virtual_current_enrichment');
          }
          if (afterSchoolStudentIds.has(student.id)) {
            addStudentToLabel(VIRTUAL_LABELS[2].label, '__virtual_current_after_school');
          }
          for (const { brand, label, labelKey } of OTHER_BRAND_LISTS) {
            if (studentIdsByOtherBrand.get(brand)?.has(student.id)) {
              addStudentToLabel(label, labelKey);
            }
          }

          if (!labels.length) continue;

          for (const rawLabel of labels) {
            const label = rawLabel.trim();
            if (!label) continue;
            const labelKey = label.toLowerCase();

            addStudentToLabel(label, labelKey);
          }
        }

        const virtualOrder = new Map<string, number>();
        VIRTUAL_LABELS.forEach((label, index) => virtualOrder.set(label.labelKey, index));
        const formatted: LabelEmailEntry[] = Array.from(labelMap.values())
          .map((entry) => ({
            label: entry.label,
            labelKey: entry.labelKey,
            students: Array.from(entry.students.values())
              .map((student) => ({
                ...student,
                parentEmails: Array.from(student.parentEmails).sort(),
              }))
              .sort((a, b) => a.name.localeCompare(b.name)),
            emails: Array.from(entry.emails).sort(),
          }))
          .sort((a, b) => {
            const orderA = virtualOrder.get(a.labelKey);
            const orderB = virtualOrder.get(b.labelKey);
            if (orderA !== undefined && orderB !== undefined) {
              return orderA - orderB;
            }
            if (orderA !== undefined) return -1;
            if (orderB !== undefined) return 1;
            return a.label.localeCompare(b.label);
          });

        setEntries(formatted);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load email lists');
      } finally {
        setLoading(false);
      }
    };

    fetchLabelEmails();
  }, []);

  const handleCopy = async (labelKey: string, emails: string[]) => {
    try {
      await navigator.clipboard.writeText(emails.join(', '));
      setCopiedLabel(labelKey);
      setTimeout(() => setCopiedLabel(null), 2000);
    } catch (err) {
      console.error('Failed to copy emails:', err);
    }
  };

  const summary = useMemo(() => {
    return { totalLabels: entries.length };
  }, [entries]);

  const virtualLabelKeys = useMemo(() => {
    return new Set(VIRTUAL_LABELS.map((label) => label.labelKey));
  }, []);

  const virtualEntries = useMemo(() => {
    return entries.filter((entry) => virtualLabelKeys.has(entry.labelKey));
  }, [entries, virtualLabelKeys]);

  const customEntries = useMemo(() => {
    return entries.filter((entry) => !virtualLabelKeys.has(entry.labelKey));
  }, [entries, virtualLabelKeys]);

  const selectedEntry = useMemo(() => {
    if (selectedLabelKeys.length === 0) return null;
    const selectedSet = new Set(selectedLabelKeys);
    const studentMap = new Map<string, { id: string; name: string; parentEmails: Set<string> }>();
    const emailSet = new Set<string>();

    for (const entry of entries) {
      if (!selectedSet.has(entry.labelKey)) continue;
      for (const student of entry.students) {
        if (!studentMap.has(student.id)) {
          studentMap.set(student.id, {
            id: student.id,
            name: student.name,
            parentEmails: new Set<string>(),
          });
        }
        const target = studentMap.get(student.id)!;
        for (const email of student.parentEmails) {
          target.parentEmails.add(email);
          emailSet.add(email);
        }
      }
    }

    const mergedStudents = Array.from(studentMap.values())
      .map((student) => ({
        ...student,
        parentEmails: Array.from(student.parentEmails).sort(),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return {
      labelKeys: selectedLabelKeys,
      students: mergedStudents,
      emails: Array.from(emailSet).sort(),
    };
  }, [entries, selectedLabelKeys]);

  const toggleLabel = (labelKey: string) => {
    setSelectedLabelKeys((prev) => {
      if (prev.includes(labelKey)) {
        return prev.filter((key) => key !== labelKey);
      }
      return [...prev, labelKey];
    });
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="text-gray-600">Loading email lists...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 lg:flex-1 lg:min-h-0 lg:overflow-y-auto">
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-lg font-semibold text-gray-900">Email Lists</h3>
        <p className="text-sm text-gray-600">
          Parent emails grouped by student label. Copy the list directly for emailing.
        </p>
        <div className="mt-2 text-sm text-gray-600">
          Labels: {summary.totalLabels}
        </div>
      </div>

      {entries.length === 0 && (
        <div className="text-gray-500">No labeled students found.</div>
      )}

      {entries.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex-1 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Built-in Labels
                </label>
                <div className="flex flex-wrap gap-2">
                  {virtualEntries.length > 0 ? (
                    virtualEntries.map((entry) => {
                      const isSelected = selectedLabelKeys.includes(entry.labelKey);
                      const baseClasses = 'px-4 py-2 rounded-full text-sm font-medium transition-colors';
                      const selectedClasses = 'bg-purple-600 text-white';
                      const idleClasses = 'bg-purple-100 text-purple-700 hover:bg-purple-200';
                      return (
                        <button
                          key={entry.labelKey}
                          type="button"
                          onClick={() => toggleLabel(entry.labelKey)}
                          className={`${baseClasses} ${isSelected ? selectedClasses : idleClasses}`}
                        >
                          {entry.label}
                        </button>
                      );
                    })
                  ) : (
                    <span className="text-sm text-gray-500">No built-in labels</span>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Labels
                </label>
                <div className="flex flex-wrap gap-2">
                  {customEntries.length > 0 ? (
                    customEntries.map((entry) => {
                      const isSelected = selectedLabelKeys.includes(entry.labelKey);
                      const baseClasses = 'px-4 py-2 rounded-full text-sm font-medium transition-colors';
                      const selectedClasses = 'bg-blue-600 text-white';
                      const idleClasses = 'bg-gray-100 text-gray-700 hover:bg-gray-200';
                      return (
                        <button
                          key={entry.labelKey}
                          type="button"
                          onClick={() => toggleLabel(entry.labelKey)}
                          className={`${baseClasses} ${isSelected ? selectedClasses : idleClasses}`}
                        >
                          {entry.label}
                        </button>
                      );
                    })
                  ) : (
                    <span className="text-sm text-gray-500">No custom labels</span>
                  )}
                </div>
              </div>
            </div>
            <div className="text-sm text-gray-600">
              Labels: {summary.totalLabels}
            </div>
          </div>

          {selectedEntry ? (
            <>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-gray-600">
                  Students: {selectedEntry.students.length} • Emails: {selectedEntry.emails.length}
                </div>
                <button
                  type="button"
                  onClick={() => handleCopy(selectedEntry.labelKeys.join('|'), selectedEntry.emails)}
                  className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                  disabled={selectedEntry.emails.length === 0}
                >
                  {copiedLabel === selectedEntry.labelKeys.join('|') ? 'Copied' : 'Copy all emails'}
                </button>
              </div>

              <div className="overflow-x-auto overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Student
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Parent Emails
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedEntry.students.map((student) => (
                      <tr key={student.id}>
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {student.name || 'Unnamed student'}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">
                          {student.parentEmails.length > 0
                            ? student.parentEmails.join(', ')
                            : 'No active parent emails'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-sm text-gray-500">Select one or more labels to view students.</div>
          )}
        </div>
      )}
    </div>
  );
}
